/**
 * ErrorBucketModal — click-to-modal drill-down for the "Error breakdown
 * (top 20)" table on the Operations tab.
 *
 * UX contract (per spec):
 *   - Click an error-row → open dialog titled `Errors · <bucket>` listing
 *     every trace that rolls up into the clicked bucket under the current
 *     filters (preset / role / cohort / channel / users). Granularity is
 *     intentionally ignored — the drill-down is per-trace, not bucketed.
 *   - Each trace row shows: timestamp, user (slack_id / username),
 *     error-message snippet, status code, latency.
 *   - Click a trace row → expand inline to show full error stack and the
 *     lazy prompt / response via `useFeedbackTraceDetail`.
 *   - Esc closes (via shadcn `Dialog` — Radix built-in).
 *   - Radix also restores focus + page scroll on close.
 *
 * Data flow:
 *   - `useErrorBucketTraces(bucket, filters)` — the list, enabled only when
 *     `bucket` is non-null.
 *   - `useFeedbackTraceDetail(traceId, expanded)` — the per-row prompt /
 *     response fetch, enabled only when a row is expanded. Reuses the
 *     existing hook so the backend contract / 5-min LRU stays shared.
 */

import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { UserLabel } from '@/components/ui/user-label';
import { useErrorBucketTraces } from '@/hooks/use-operations';
import { useFeedbackTraceDetail } from '@/hooks/use-feedback-trace-detail';
import type { Filters } from '@/lib/filters-schema';
import { formatIsoLikeCet } from '@/lib/format-date';
import { formatLatencyMs } from '@/lib/format';
import { errorClassLabel } from '@/lib/error-class-labels';
import { cn } from '@/lib/utils';
import type { ErrorBucketTraceRow } from '@/services/api-types';

interface ErrorBucketModalProps {
  bucket: string | null;
  filters: Filters;
  /** Fires when Radix/Esc/close-button closes the dialog. */
  onClose: () => void;
}

function formatTs(iso: string): string {
  return formatIsoLikeCet(iso);
}

/**
 * Inline identity for the error trace meta strip. Prefers the directory
 * display name, then the handle, then the slack ID as the last resort.
 * UserLabel takes over primary Name + @handle rendering; slackId is shown
 * in parens when that's the only ID we have.
 */
function UserCell({
  row,
}: {
  row: ErrorBucketTraceRow;
}): React.ReactElement {
  if (row.userId) {
    return (
      <UserLabel
        name={row.realName}
        username={row.userId}
        variant="inline"
        testId="error-bucket-trace-user"
      />
    );
  }
  if (row.slackId) {
    return (
      <span
        data-testid="error-bucket-trace-user"
        className="font-medium text-foreground"
      >
        {row.slackId}
      </span>
    );
  }
  return (
    <span
      data-testid="error-bucket-trace-user"
      className="font-medium text-foreground"
    >
      —
    </span>
  );
}

/**
 * One trace row. Collapsed by default; click to toggle. When expanded we
 * lazy-fetch the Langfuse prompt/response via the shared Feedback endpoint.
 */
function TraceRow({
  row,
}: {
  row: ErrorBucketTraceRow;
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const detail = useFeedbackTraceDetail(row.traceId, expanded);

  const toggle = React.useCallback(() => setExpanded((v) => !v), []);

  const errorSnippet =
    row.error && row.error.length > 140
      ? `${row.error.slice(0, 137)}…`
      : (row.error ?? '—');

  return (
    <li
      data-testid="error-bucket-trace-row"
      data-trace-id={row.traceId}
      data-expanded={expanded ? 'true' : 'false'}
      className={cn(
        'rounded-md border border-border bg-card text-sm',
        expanded && 'ring-1 ring-primary/40',
      )}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid="error-bucket-trace-row-toggle"
        className="flex w-full flex-col gap-1 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="font-mono">{formatTs(row.timestamp)}</span>
          <span>·</span>
          <UserCell row={row} />
          {row.statusCode !== null && (
            <>
              <span>·</span>
              <span>HTTP {row.statusCode}</span>
            </>
          )}
          {row.latencyMs !== null && (
            <>
              <span>·</span>
              <span>{formatLatencyMs(row.latencyMs)}</span>
            </>
          )}
          {row.level && (
            <>
              <span>·</span>
              <span className="uppercase tracking-wide">{row.level}</span>
            </>
          )}
          {row.name && (
            <>
              <span>·</span>
              <span>{row.name}</span>
            </>
          )}
        </div>
        <div className="text-sm text-foreground break-words">
          {errorSnippet}
        </div>
      </button>

      {expanded && (
        <div
          data-testid="error-bucket-trace-row-detail"
          className="space-y-3 border-t border-border bg-muted/30 p-3 text-xs"
        >
          {row.error && (
            <section>
              <div className="mb-1 font-semibold text-muted-foreground">
                Full error
              </div>
              <pre className="whitespace-pre-wrap break-words rounded border border-border bg-background p-2 font-mono leading-snug">
                {row.error}
              </pre>
            </section>
          )}

          <section>
            <div className="mb-1 font-semibold text-muted-foreground">
              Prompt / response (Langfuse)
            </div>
            {detail.isLoading && (
              <div
                data-testid="error-bucket-trace-detail-loading"
                className="rounded border border-border bg-background p-2 text-muted-foreground"
              >
                Loading…
              </div>
            )}
            {detail.isError && (
              <div
                data-testid="error-bucket-trace-detail-error"
                className="flex items-center gap-2 rounded border border-destructive/40 bg-background p-2 text-destructive"
              >
                <span>Failed to load trace detail.</span>
                <button
                  type="button"
                  onClick={() => void detail.refetch()}
                  className="underline hover:no-underline"
                >
                  Retry
                </button>
              </div>
            )}
            {detail.data && !detail.isLoading && !detail.isError && (
              <div className="space-y-2">
                {detail.data.error && (
                  <div className="rounded border border-border bg-background p-2 text-destructive">
                    {detail.data.error}
                  </div>
                )}
                <div>
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Input
                  </div>
                  <pre className="whitespace-pre-wrap break-words rounded border border-border bg-background p-2 font-mono leading-snug max-h-60 overflow-auto">
                    {detail.data.input ?? '—'}
                  </pre>
                </div>
                <div>
                  <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Output
                  </div>
                  <pre className="whitespace-pre-wrap break-words rounded border border-border bg-background p-2 font-mono leading-snug max-h-60 overflow-auto">
                    {detail.data.output ?? '—'}
                  </pre>
                </div>
              </div>
            )}
          </section>

          <div className="text-[11px] text-muted-foreground">
            traceId: <span className="font-mono">{row.traceId}</span>
          </div>
        </div>
      )}
    </li>
  );
}

export function ErrorBucketModal({
  bucket,
  filters,
  onClose,
}: ErrorBucketModalProps): React.ReactElement {
  // React Query hook; inert while `bucket === null`.
  const traces = useErrorBucketTraces(bucket, filters, { limit: 200 });

  const open = bucket !== null;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="error-bucket-modal"
        data-bucket={bucket ?? ''}
        // Wide enough to read a stack trace; tall-ish with internal scroll.
        className="max-w-3xl w-[min(96vw,48rem)] max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle
            data-testid="error-bucket-modal-title"
            // Native title shows the raw bucket ID so operators can copy
            // the exact value back into a SQL filter / log grep.
            title={bucket ?? ''}
          >
            Errors · {errorClassLabel(bucket)}
          </DialogTitle>
          {/* Operations P1-4 — use `DialogDescription` instead of a raw `<p>`
              so Radix wires `aria-describedby` to this node and screen
              readers read the real subtitle (not the generic auto-emitted
              "Dialog content." placeholder from `ui/dialog.tsx`). */}
          <DialogDescription
            data-testid="error-bucket-modal-subtitle"
            className="text-xs text-muted-foreground"
          >
            Every trace in the selected window and filters that fell into this
            bucket. Click a trace to see the full error and prompt/response.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {traces.isLoading && (
            <div
              data-testid="error-bucket-modal-loading"
              className="space-y-2 p-1"
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-14 rounded-md border border-border bg-muted/50 animate-pulse"
                />
              ))}
            </div>
          )}

          {traces.isError && (
            <div
              data-testid="error-bucket-modal-error"
              className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-background p-3 text-sm text-destructive"
            >
              <span>Failed to load traces for this bucket.</span>
              <button
                type="button"
                onClick={() => void traces.refetch()}
                className="underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          {traces.data && traces.data.length === 0 && !traces.isLoading && (
            <div
              data-testid="error-bucket-modal-empty"
              className="rounded-md border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
            >
              No traces match this bucket for the current filters.
            </div>
          )}

          {traces.data && traces.data.length > 0 && (
            <ul
              data-testid="error-bucket-modal-list"
              className="list-none space-y-2 p-1"
            >
              {traces.data.map((row) => (
                <TraceRow key={row.traceId} row={row} />
              ))}
            </ul>
          )}
        </div>

        {traces.data && traces.data.length > 0 && (
          <div
            data-testid="error-bucket-modal-footer"
            className="border-t border-border pt-2 text-xs text-muted-foreground"
          >
            Showing {traces.data.length} trace
            {traces.data.length === 1 ? '' : 's'}
            {(() => {
              const rm = traces.data.filter((r) => r.isRm).length;
              const nonRm = traces.data.length - rm;
              return (
                <>
                  <span className="mx-1">·</span>
                  RM{' '}
                  <span className="font-semibold text-foreground">{rm}</span>
                  {' · '}
                  Non-RM{' '}
                  <span className="font-semibold text-foreground">{nonRm}</span>
                </>
              );
            })()}
            .
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
